import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, resolveImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const buildSections = (venueName) => [
  {
    heading: "About Us",
    body: [
      `The event is organised and operated by ${venueName}.`,
      `Tickets are sold through the Affra platform on behalf of ${venueName}.`,
      "Affra acts solely as the ticketing platform and payment processor for the Venue and is not responsible for the organisation, operation or delivery of the event.",
    ],
  },
  { heading: "Ticket Purchase", body: [
    "When you purchase a ticket, a legally binding contract is formed between you and the Venue.",
    "Your booking confirmation serves as proof of purchase.",
    "Please check your booking carefully before completing payment.",
  ] },
  { heading: "Prices", body: [
    "All ticket prices displayed include all mandatory booking and transaction charges payable by the consumer unless clearly stated otherwise in accordance with applicable law.",
    "Optional products or services, including donations, parking, merchandise or ticket protection, will be clearly identified before purchase and will not be pre-selected.",
  ] },
  { heading: "Payment", body: [
    "Payment must be made in full before tickets are issued.",
    "Payments are processed securely through PCI DSS-compliant payment providers.",
    "The Venue does not store full payment card details.",
  ] },
  { heading: "Ticket Delivery", body: [
    "Tickets may be delivered electronically, by mobile ticket or by any other method specified during checkout.",
    "Customers are responsible for ensuring that contact details supplied during booking are accurate.",
  ] },
  { heading: "Entry Conditions", body: ["Valid tickets must be presented for admission.", "The Venue reserves the right to:"],
    list: ["refuse admission where tickets are invalid;", "refuse admission where health, safety or licensing requirements require;", "remove persons behaving in an abusive, dangerous or unlawful manner."],
    after: "Admission may also be subject to age restrictions or identification requirements." },
  { heading: "Customer Conduct", body: ["Customers must comply with all venue rules and staff instructions.", "The Venue reserves the right to refuse entry or remove any individual whose behaviour may:"],
    list: ["endanger others;", "damage property;", "disrupt the event;", "breach applicable laws."],
    after: "Removal under this clause may occur without refund where permitted by law." },
  { heading: "Event Changes", body: ["We reserve the right to make reasonable changes to:"],
    list: ["event timings;", "performers;", "schedules;", "seating arrangements;", "event content,"],
    after: "where necessary due to circumstances beyond our reasonable control. Minor changes do not normally entitle customers to a refund. Where a significant change materially affects the nature of the event, customers will be informed as soon as reasonably practicable and any available remedies will be communicated." },
  { heading: "Event Cancellation, Postponement, Changes and Refunds",
    groups: [
      { title: "9.1 Event Cancellation", intro: "If an event is cancelled by the Venue and is not rescheduled, customers will normally be entitled to a refund of the face value of the ticket and any mandatory booking fees paid, unless otherwise permitted by applicable law. Refunds will be processed using the original payment method wherever reasonably practicable. The Venue aims to process eligible refunds within 14 days of confirming the cancellation, although processing times may vary depending on the payment provider." },
      { title: "9.2 Event Postponement or Rescheduling", intro: "Where an event is postponed or rescheduled, purchased tickets will normally remain valid for the new date. If you are unable to attend the rescheduled event, the Venue will advise whether a refund, ticket exchange, credit or alternative remedy is available in accordance with applicable consumer law and the Venue's published refund policy. Customers should retain their tickets until further instructions are issued." },
      { title: "9.3 Changes to an Event", intro: "The Venue reserves the right to make reasonable changes where necessary, including changes to:", items: ["Event timings;", "Running order;", "Supporting acts;", "Speakers or performers;", "Seating arrangements;", "Venue layout;", "Event format."], footer: "Minor changes which do not materially affect the overall nature of the event will not normally entitle customers to a refund. Where a significant change substantially alters the event purchased, the Venue will notify customers as soon as reasonably practicable and advise of any available remedies." },
      { title: "9.4 Customer Cancellations", intro: "Tickets are generally sold for leisure services taking place on a specific date and are therefore exempt from the statutory 14-day cancellation period under the Consumer Contracts Regulations 2013. Except where required by law or expressly stated by the Venue, tickets cannot be cancelled, refunded or exchanged simply because a customer:", items: ["Changes their mind;", "Is unable to attend;", "Has made alternative arrangements;", "Arrives late;", "Fails to attend."] },
      { title: "9.5 Ticket Transfers", intro: "Whether tickets may be transferred to another individual is determined by the Venue. Where transfers are permitted, they may be subject to identity verification, transfer deadlines or administrative charges where permitted by law." },
      { title: "9.6 Duplicate Purchases", intro: "Customers should review their order carefully before completing payment. Duplicate bookings resulting from accidental multiple purchases may be refunded at the Venue's discretion, provided the request is made promptly and the tickets have not been used." },
      { title: "9.7 Refused Admission", intro: "Refunds will not normally be provided where admission is refused because a customer:", items: ["Does not possess a valid ticket;", "Fails to comply with age restrictions;", "Is intoxicated;", "Behaves aggressively, abusively or unlawfully;", "Breaches venue rules or licensing conditions;", "Presents a health or safety risk."], footer: "Nothing within this clause limits any statutory rights that cannot legally be excluded." },
      { title: "9.8 Force Majeure", intro: "The Venue shall not be liable for delays, postponements or cancellations resulting from circumstances beyond its reasonable control, including but not limited to:", items: ["Severe weather;", "Flooding;", "Fire;", "Industrial action;", "Government restrictions;", "Public health emergencies;", "Terrorism;", "Utility failures;", "Acts of God;", "Venue damage."], footer: "Where an event is affected by a Force Majeure event, the Venue will use reasonable endeavours to reschedule the event or provide an appropriate remedy where required by law." },
      { title: "9.9 Refund Method", intro: "Approved refunds will normally be made to the original payment method used during purchase. Refunds cannot normally be paid to alternative payment methods unless required by law or agreed by the Venue." },
      { title: "9.10 Booking Fees", intro: "Mandatory booking or transaction fees will only be refunded where required by law or where the Venue has expressly agreed to do so. Optional products or services purchased during checkout, including donations, ticket protection, merchandise or parking, may be subject to separate refund policies." },
      { title: "9.11 Statutory Rights", intro: "Nothing within these Terms limits or excludes your statutory rights under the Consumer Rights Act 2015, the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013, or any other applicable consumer protection legislation." },
    ] },
  { heading: "Consumer Cancellation Rights", body: [
    "Under the Consumer Contracts Regulations 2013, tickets for leisure activities provided on a specific date or within a specific period are generally exempt from the statutory 14-day cooling-off cancellation period.",
    "Accordingly, once your purchase has been confirmed, you will not normally have a statutory right to cancel your booking simply because you have changed your mind.",
    "This does not affect your legal rights where the event is cancelled, significantly changed, or where the Venue is otherwise in breach of contract.",
  ] },
  { heading: "Accessibility", body: [
    "Customers requiring accessibility assistance are encouraged to contact the Venue before purchasing tickets where possible.",
    "We will make reasonable adjustments in accordance with applicable equality legislation.",
  ] },
  { heading: "Photography and Recording", body: [
    "Photography, filming or recording may take place during events.",
    "By attending, you acknowledge that you may appear incidentally in photographs or recordings used for legitimate promotional, security or archival purposes.",
    "Where legally required, appropriate notices will be displayed.",
  ] },
  { heading: "Personal Property", body: [
    "Customers remain responsible for their own personal belongings.",
    "The Venue accepts no responsibility for lost, stolen or damaged property except where liability cannot legally be excluded.",
  ] },
  { heading: "Liability", body: ["Nothing within these Terms limits or excludes liability for:"],
    list: ["death or personal injury caused by negligence;", "fraud or fraudulent misrepresentation;", "any liability that cannot legally be excluded."],
    after: "Subject to the above, the Venue's liability shall be limited to the amount paid for the relevant ticket except where otherwise required by law." },
  { heading: "Personal Information", body: [
    "Personal information will be processed in accordance with the Venue's Privacy Policy.",
    "Ticketing services are provided by Affra, which processes information on behalf of the Venue as its data processor.",
  ] },
  { heading: "Fraud Prevention", body: [
    "The Venue reserves the right to cancel bookings where fraud, payment irregularities or unauthorised use of payment methods is reasonably suspected.",
    "Refunds may be withheld pending investigation where permitted by law.",
  ] },
  { heading: "Force Majeure", body: [
    "The Venue shall not be liable for delays, cancellations or changes resulting from circumstances beyond its reasonable control, including but not limited to severe weather, natural disasters, industrial disputes, public health emergencies, government action or utility failures.",
    "Where applicable, remedies will be provided in accordance with consumer law.",
  ] },
  { heading: "Governing Law", body: [
    "These Terms are governed by the laws of England and Wales.",
    "Any dispute shall be subject to the exclusive jurisdiction of the courts of England and Wales.",
    "Nothing in these Terms affects any mandatory consumer rights available under applicable law.",
  ] },
  { heading: "Contact", body: [
    "For any questions regarding your booking, the event, refunds or these Terms & Conditions, please contact the Venue using the contact details published on its official website.",
  ] },
];

export default function VenueTerms() {
  const { slug } = useParams();
  const [venue, setVenue] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/venues/${slug}`);
        setVenue(data.venue);
      } catch { setErr("Venue not found"); }
    })();
  }, [slug]);

  if (err) return <div className="min-h-screen grid place-items-center text-zinc-500">{err}</div>;
  if (!venue) return <div className="min-h-screen grid place-items-center text-zinc-500 text-sm">Loading…</div>;

  const brand = venue.brand_color || "#4338CA";
  const sections = buildSections(venue.name);
  const logo = resolveImageUrl(venue.logo_url);

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to={`/v/${venue.slug}`} className="flex items-center gap-2 font-display font-black text-lg tracking-tight">
            {logo ? (
              <img src={logo} alt={venue.name} className="h-8 w-8 rounded-md object-contain" />
            ) : (
              <span className="h-8 w-8 grid place-items-center rounded-md text-white font-black" style={{ background: brand }}>
                {venue.name?.[0] || "V"}
              </span>
            )}
            {venue.name}
          </Link>
          <Link to={`/v/${venue.slug}`}>
            <Button variant="ghost" className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to venue
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        <div className="text-sm font-mono uppercase tracking-widest" style={{ color: brand }}>/ legal</div>
        <h1 className="mt-3 font-display font-black text-4xl lg:text-6xl tracking-tighter leading-none">Ticket Purchase Terms &amp; Conditions</h1>
        <div className="mt-4 text-sm text-zinc-500">Effective Date: 10 July 2026</div>

        <p className="mt-8 text-[15px] leading-relaxed text-zinc-700">
          These Terms &amp; Conditions govern the purchase and use of tickets for events organised by <strong>{venue.name}</strong> (&quot;the Venue&quot;, &quot;we&quot;, &quot;our&quot; or &quot;us&quot;).
        </p>
        <p className="mt-3 text-[15px] leading-relaxed text-zinc-700">By purchasing tickets, you agree to these Terms.</p>

        <div className="mt-12 space-y-10">
          {sections.map((s, i) => (
            <section key={s.heading} className="scroll-mt-24" id={`section-${i + 1}`}>
              <h2 className="font-display font-bold text-2xl tracking-tight flex gap-3 items-baseline">
                <span className="text-zinc-400 font-mono text-lg tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                <span>{s.heading}</span>
              </h2>
              <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-zinc-700 pl-9">
                {s.body?.map((p, idx) => <p key={idx}>{p}</p>)}
                {s.list && (
                  <ul className="list-disc pl-5 space-y-1.5 marker:text-zinc-400">
                    {s.list.map((li, idx) => <li key={idx}>{li}</li>)}
                  </ul>
                )}
                {s.groups && (
                  <div className="space-y-6">
                    {s.groups.map((g, idx) => (
                      <div key={idx}>
                        <div className="font-semibold text-zinc-900">{g.title}</div>
                        {g.intro && <p className="mt-1">{g.intro}</p>}
                        {g.items && (
                          <ul className="mt-2 list-disc pl-5 space-y-1.5 marker:text-zinc-400">
                            {g.items.map((it, ii) => <li key={ii}>{it}</li>)}
                          </ul>
                        )}
                        {g.footer && <p className="mt-2">{g.footer}</p>}
                      </div>
                    ))}
                  </div>
                )}
                {s.after && <p>{s.after}</p>}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-zinc-200 text-sm text-zinc-500">
          See also{" "}
          <Link to={`/v/${venue.slug}/privacy`} className="hover:underline" style={{ color: brand }}>{venue.name}&apos;s Privacy Policy</Link>{" "}
          and{" "}
          <Link to="/terms" className="hover:underline" style={{ color: brand }}>Affra&apos;s platform terms</Link>.
        </div>
      </main>

      <footer className="border-t border-zinc-200 py-6 mt-8">
        <div className="max-w-5xl mx-auto px-6 text-xs text-zinc-500 flex items-center justify-between flex-wrap gap-2">
          <div>© {new Date().getFullYear()} {venue.name}</div>
          <div>Powered by <a href="/" className="text-zinc-700 hover:text-zinc-900">Affra</a></div>
        </div>
      </footer>
    </div>
  );
}
